import { ReactElement, useState } from 'react';
import * as React from 'react';
import {
  Chart,
  BarSeries,
  ValueAxis,
  LineSeries,
  ScatterSeries,
  ZoomAndPan,
  ArgumentAxis,
  Tooltip,
} from '@devexpress/dx-react-chart-material-ui';
import {
  AreaSeries,
  ArgumentScale,
  EventTracker,
  SeriesRef,
  ValueScale,
  Viewport,
} from '@devexpress/dx-react-chart';
import { green, purple } from 'globals/colors';
import {
  getDoubleRoundBarSeriesPointComponent,
  getTooltipContentComponent,
  scaleDateFactory,
} from 'globals/graph-utils';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { getDateDiffInDays } from 'globals/utils';
import {
  FsConversionChartSingleRowDataProps,
  getInitialDayRange,
  getZoomLevel,
  modifyDomain,
} from './FsConversionChart.utils';
import {
  ValueAxisGridComponent,
  ValueAxisLabelComponent,
  ArgumentAxisLabelComponent,
  TooltipSheetComponent,
  TooltipOverlay,
} from './FsConversionChart.style-elements';

interface FsConversionChartDataProps {
  chartData: FsConversionChartSingleRowDataProps[];
}

interface FsConversionChartProps extends FsConversionChartDataProps {
  mainColor?: string;
}

const FsConversionChart: React.FC<FsConversionChartProps> = ({
  chartData,
  mainColor = green.MAIN,
}) => {
  const [targetItem, setTargetItem] = useState<SeriesRef>();
  const initialDayRange = getInitialDayRange(chartData);
  const [viewport, setViewport] = useState<Viewport>({});
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const theme = useTheme();
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  // TODO: i need to get such cashflow-summary from API
  // const currency = 'USD';
  // const format = 'en-US';

  const changeTargetItem = (target?: SeriesRef): void => {
    if (target && target?.series === 'scatterSeries') return;
    setTargetItem(target);
  };

  const changeViewport = (v: Viewport): void => {
    if (v.argumentStart && v.argumentEnd) {
      const newDayRange = getDateDiffInDays(v.argumentStart, v.argumentEnd);
      const zoomLevelValue = getZoomLevel(initialDayRange, newDayRange);
      if (zoomLevelValue < 20) {
        setZoomLevel(zoomLevelValue);
        setViewport(v);
      }
    }
  };

  const BarSeriesPointComponent = getDoubleRoundBarSeriesPointComponent({
    barWidth: zoomLevel > 2 ? Math.min(zoomLevel * 10, 40) : 12,
    rx: zoomLevel > 2 ? Math.min(zoomLevel * 10, 40) / 2 : 6,
    ry: zoomLevel > 2 ? Math.min(zoomLevel * 10, 40) / 2 : 6,
  });
  const TooltipContentComponent = getTooltipContentComponent({
    chartData,
    label: 'Accepted',
  });

  return (
    <Chart data={chartData} height={410}>
      {/* <p>ZOOM LEVEL: {zoomLevel}</p> */}
      {/* X */}
      <ArgumentScale factory={scaleDateFactory()} />
      <ArgumentAxis
        showLine={false}
        labelComponent={ArgumentAxisLabelComponent}
        indentFromAxis={10}
      />
      {/* Y */}
      <ValueScale name="valueScale" modifyDomain={modifyDomain} />
      <ValueAxis
        labelComponent={ValueAxisLabelComponent}
        scaleName="valueScale"
        gridComponent={ValueAxisGridComponent}
        indentFromAxis={30}
        // tickFormat={amountTickFormatterForValueAxis({ format, currency })}
      />

      {/* LINE */}
      <LineSeries
        name="conversionLine"
        valueField="y"
        argumentField="x"
        color="#D6BDF0"
        scaleName="valueScale"
      />

      {/* AREA */}
      <AreaSeries
        name="conversionArea"
        valueField="y"
        argumentField="x"
        color="#F6F3FA"
        scaleName="valueScale"
      />

      {/* DOTS */}
      <ScatterSeries
        name="conversionDots"
        valueField="y"
        argumentField="x"
        color={purple.BRAND}
        point={{ size: 2 }}
        scaleName="valueScale"
      />

      {/* BARS */}
      <BarSeries
        name="conversionBars"
        valueField="y"
        argumentField="x"
        color={mainColor}
        scaleName="valueScale"
        pointComponent={BarSeriesPointComponent}
      />

      {/* EVENT TRACKER */}
      <EventTracker />

      {/* ZOOM AND PAN */}
      {isUpSm && (
        <ZoomAndPan viewport={viewport} onViewportChange={changeViewport} />
      )}

      {/* TOOLTIP */}
      <Tooltip
        targetItem={targetItem}
        onTargetItemChange={changeTargetItem}
        sheetComponent={TooltipSheetComponent}
        contentComponent={TooltipContentComponent}
        overlayComponent={(props): ReactElement<Tooltip.OverlayProps> => (
          <TooltipOverlay {...props} />
        )}
      />
    </Chart>
  );
};

export default FsConversionChart;
